<template>
  <div id="order-mutation-policy-sheet" class="order-detail-help-content">
    <div v-show="order.cancel_reschedule_text" class="wd-px-5 sm:(wd-px-0) wd-pt-4 wd-whitespace-pre-line">
      {{ order.cancel_reschedule_text }}
    </div>

    <div class="content-block cancel--btn" @click="showPolicyModal">
      <div class="content-block__context wd-flex wd-flex-nowrap wd-justify-start wd-items-center">
        <span class="wd-mr-1"> {{ $t('all_cancellation_field_button_rule_content') }}</span>
        <FunnowSvgIcon class="wd-text-orange50" max-width="20" height="20" image-path="chevron-right.svg" />
      </div>
    </div>

    <div v-if="reschedulable" class="content-block cancel--btn" @click="openOrderMutationModal('reschedule')">
      <div class="content-block__context">
        <span>
          {{
            isOrderHeadcountChangeable ? $t('cancellation_field_text_button_change_order') : $t('all_cancellation_field_button_changetime_content')
          }}
        </span>
      </div>
    </div>

    <div v-if="cancellable" class="content-block cancel--btn" @click="openOrderMutationModal('cancellation')">
      <div class="content-block__context">
        <span> {{ cancellationButtonText }} </span>
      </div>
    </div>

    <cancellation-policy-modal
      ref="cancellationPolicyModal"
      :is-delivery="isDelivery"
      :is-movie="isMovie"
      :cancel-rules="order.cancel_rules"
      :reschedule-rules="order.reschedule_rules"
    />
    <cancel-order-modal ref="cancelOrderModal" :order="order" :order-cancel-info="cancelData" :token="token" />
    <contact-us-modal ref="contactUsModal" />
    <OrderRescheduleModal
      v-model="showRescheduleModal"
      :reschedulable="rescheduleData.reschedulable"
      :hint-text="rescheduleData.rescheduleRule"
      :order="order"
      :token="token"
    />
  </div>
</template>

<script>
import { templateRef } from '@vueuse/core'
import CancellationPolicyModal from '@/components/dialog/orders/cancellation-policy-modal.vue'
import CancelOrderModal from '@/components/dialog/orders/cancel-order-modal.vue'
import ContactUsModal from '@/components/dialog/orders/contact-us-modal.vue'
import FunnowSvgIcon from '@/components/icons/funnow-svg-icon.vue'
import OrderRescheduleModal from '@/components/OrderRescheduleModal.vue'
import { useOrderDetailContext, useOrderFlags } from '@/composition/useOrder'
import { OrderMovieStatus } from '@/view-models/order'
import { useAnonymousOrder } from '@/composition/useAnonymousOrder'
import * as OrderService from '@/service/order'

export default defineComponent({
  name: 'OrderMutationPolicySheet',
  components: {
    CancellationPolicyModal,
    CancelOrderModal,
    ContactUsModal,
    FunnowSvgIcon,
    OrderRescheduleModal,
  },
  props: {
    order: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const nuxtApp = useNuxtApp()
    const cancellationPolicyModal = templateRef('cancellationPolicyModal')
    const cancelOrderModal = templateRef('cancelOrderModal')

    const showPureHintModal = ref(false)
    const showRescheduleModal = ref(false)
    const rescheduleData = reactive({
      reschedulable: false,
      rescheduleRule: '',
    })
    const cancelData = reactive({
      cancelable: false,
      cancel_rule: '',
    })

    const order = computed(() => props.order)
    const { isMovie, isCamping, isDelivery, isOrderHeadcountChangeable } = useOrderFlags(order)
    const reschedulable = computed(() => !!props.order.reschedulable)

    const { checkOrderMutation } = useAnonymousOrder(order)
    const { token } = useOrderDetailContext()

    const cancellable = computed(() => {
      if (isDelivery.value) {
        return false
      }
      if (isMovie.value) {
        return props.order.movie.status === OrderMovieStatus.BookingSuccess
      }
      return order.value.cancelable
    })

    const cancellationButtonText = computed(() => {
      if (isMovie.value) {
        return nuxtApp.$i18n.t('movie_cancel_title')
      }
      return nuxtApp.$i18n.t('cancel_booking_cancel_button')
    })

    function showPolicyModal() {
      cancellationPolicyModal.value.showModal()
    }

    // get api, assign data, open modal
    const orderMutationStrategyMap = {
      reschedule: {
        getRuleInfo: OrderService.getOrderRescheduleInfo,
        updateData(data) {
          rescheduleData.reschedulable = data.reschedulable
          rescheduleData.rescheduleRule = data.reschedule_rule
        },
        openModal() {
          showRescheduleModal.value = true
        },
      },
      cancellation: {
        getRuleInfo: OrderService.getOrderCancelInfo,
        updateData(data) {
          cancelData.cancelable = data.cancelable
          cancelData.cancel_rule = data.cancel_rule
        },
        openModal() {
          cancelOrderModal.value.showModal()
        },
      },
    }

    /**
     *
     * @param {'reschedule' | 'cancellation'} strategy
     */
    async function openOrderMutationModal(strategy) {
      const strategyPayload = orderMutationStrategyMap[strategy]
      if (!strategyPayload) {
        return
      }

      const { getRuleInfo, updateData, openModal } = strategyPayload
      try {
        if (!checkOrderMutation()) {
          return
        }
        const res = await getRuleInfo(nuxtApp.$axios, { orderId: order.value.id, token: token.value })
        updateData(res.data)
        openModal()
      } catch (error) {
        nuxtApp.$store.dispatch('dialog/setRootMessageOption', { status: true, options: { message: error.message } })
        console.error(error)
      }
    }

    return {
      isDelivery,
      isMovie,
      isCamping,
      cancellable,
      reschedulable,
      isOrderHeadcountChangeable,
      cancellationButtonText,
      showPureHintModal,
      showRescheduleModal,
      showPolicyModal,
      checkOrderMutation,
      token,
      rescheduleData,
      cancelData,
      openOrderMutationModal,
    }
  },
})
</script>
<style lang="scss" scoped>
.order-detail-help-content {
  .content-block {
    cursor: pointer;

    &.cancel--btn {
      color: $orange50;
    }
  }
}
</style>
